import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SyncIcon from "@mui/icons-material/Sync";
import type {
  AdDeliveryReport,
  AdDayPoint,
  VistarCatalogueStatus,
  VistarCatalogueSyncView,
} from "@shared/types/playbackAnalytics";
import { tokens } from "../../theme";
import { useFormat } from "../../i18n/format";
import { ChangeBadge, KpiTile, TrendLine } from "./charts";
import { usePlaybackFormat, type PlaybackFormat } from "./format";

/**
 * Analytics → Content analytics → Ads: proof of play for advertising.
 *
 * Two sources, shown apart and never added (see `AdDeliveryReport`):
 * Foyer's player counts an ad its player CONFIRMED and Vistar ACCEPTED; the
 * legacy autoplayer can only say an ad STARTED. The page says which is which
 * in words, next to the numbers, because these are the figures an advertiser
 * is billed on.
 */

const EMPTY = "–";
const SEPARATOR = " · ";
const FILTER_KEYS = [
  "chain",
  "kind",
  "provider",
  "collection",
  "asset",
  "device",
] as const;

/** Every day of the window, zeros included: a gap is a day without ads. */
function fillDays(
  pf: PlaybackFormat,
  from: string,
  to: string,
  pts: AdDayPoint[]
) {
  const by = new Map(pts.map((p) => [p.day, p]));
  const out: { label: string; value: number }[] = [];
  const d = new Date(`${from}T00:00:00Z`);
  const end = new Date(`${to}T00:00:00Z`);
  for (let i = 0; d <= end && i < 1200; i++) {
    const day = d.toISOString().slice(0, 10);
    out.push({ label: pf.bucket(day, "day"), value: by.get(day)?.played ?? 0 });
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return out;
}

function Section({
  title,
  subtitle,
  children,
  testId,
}: {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <Card variant="outlined" data-testid={testId}>
      <CardContent>
        <Typography variant="subtitle1">{title}</Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {subtitle}
          </Typography>
        )}
        {children}
      </CardContent>
    </Card>
  );
}

interface Column<T> {
  label: string;
  align?: "right";
  cell: (row: T) => ReactNode;
}

function SimpleTable<T>({
  rows,
  rowKey,
  columns,
  empty,
  testId,
}: {
  rows: T[];
  rowKey: (row: T) => string;
  columns: Column<T>[];
  empty: string;
  testId?: string;
}) {
  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" data-testid={testId}>
        {empty}
      </Typography>
    );
  }
  return (
    <TableContainer sx={{ overflowX: "auto" }} data-testid={testId}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map((c) => (
              <TableCell
                key={c.label}
                align={c.align}
                sx={{ whiteSpace: "nowrap" }}
              >
                {c.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={rowKey(r)}>
              {columns.map((c) => (
                <TableCell
                  key={c.label}
                  align={c.align}
                  sx={{ fontVariantNumeric: "tabular-nums" }}
                >
                  {c.cell(r)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/** What both players know about one Vistar creative. */
interface CreativeFacts {
  creativeName: string | null;
  advertiser: string | null;
  creativeId: string | null;
  campaignId: string | null;
  vistarAssetId: string | null;
  assetUrl: string | null;
  durationSeconds: number | null;
}

/**
 * Which ad it is, in the best words Vistar gave us: its creative name (from
 * the Vistar catalogue) with the advertiser beside it; else the advertiser;
 * else the Vistar creative id. Always with a preview of the video, so a
 * person can tell which ad it is even when Vistar named nothing.
 */
function CreativeCell({ c }: { c: CreativeFacts }) {
  const { t } = useTranslation(["analytics", "common"]);
  const [previewing, setPreviewing] = useState(false);
  const id = c.creativeId ?? c.vistarAssetId;
  const primary =
    c.creativeName ??
    c.advertiser ??
    (id
      ? t("adDelivery.creative.fallbackWithId", { id })
      : t("adDelivery.creative.fallback"));
  const details = [
    c.creativeName && c.advertiser ? c.advertiser : null,
    c.campaignId
      ? t("adDelivery.creative.campaign", { id: c.campaignId })
      : null,
    c.durationSeconds
      ? t("playbackFormat.seconds", { value: c.durationSeconds })
      : null,
  ].filter(Boolean);
  return (
    <Box>
      <Typography variant="body2">{primary}</Typography>
      <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
        {details.join(SEPARATOR)}
        {c.assetUrl && (
          <>
            {details.length > 0 && SEPARATOR}
            <Link
              component="button"
              type="button"
              onClick={() => setPreviewing(true)}
              underline="hover"
              sx={{ fontSize: "inherit", verticalAlign: "baseline" }}
              data-testid="ads-preview-open"
            >
              {t("common:buttons.preview")}
            </Link>
          </>
        )}
      </Typography>
      {c.assetUrl && (
        <AdPreviewDialog
          open={previewing}
          onClose={() => setPreviewing(false)}
          url={c.assetUrl}
          title={[primary, ...details].join(SEPARATOR)}
        />
      )}
    </Box>
  );
}

const IMAGE_URL = /\.(jpe?g|png|gif|webp)(\?|#|$)/i;

/**
 * The creative, played on the page: Vistar creatives are mp4 (images from
 * the catalogue are shown as images). Starts muted so the browser lets it
 * autoplay; the controls unmute and scrub. Closing unmounts the video, so it
 * stops - nothing keeps playing behind the page.
 */
export function AdPreviewDialog({
  open,
  onClose,
  url,
  title,
}: {
  open: boolean;
  onClose: () => void;
  url: string;
  title: string;
}) {
  const { t } = useTranslation("analytics");
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      aria-label={t("adDelivery.preview.aria", { title })}
      data-testid="ads-preview"
    >
      {open && (
        <Box
          sx={{
            position: "relative",
            bgcolor: "#000",
            display: "grid",
            placeItems: "center",
            minWidth: 320,
            minHeight: 240,
          }}
        >
          <IconButton
            onClick={onClose}
            aria-label={t("adDelivery.preview.close")}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 1,
              bgcolor: "rgba(0,0,0,0.5)",
              color: "white",
              "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
            }}
            data-testid="ads-preview-close"
          >
            <CloseIcon />
          </IconButton>
          {IMAGE_URL.test(url) ? (
            <img
              src={url}
              alt={title}
              style={{
                display: "block",
                maxWidth: "90vw",
                maxHeight: "80vh",
                objectFit: "contain",
              }}
            />
          ) : (
            <video
              src={url}
              controls
              autoPlay
              muted
              playsInline
              aria-label={title}
              data-testid="ads-preview-video"
              style={{
                display: "block",
                maxWidth: "90vw",
                maxHeight: "80vh",
                objectFit: "contain",
              }}
            />
          )}
          <Typography
            variant="caption"
            sx={{
              display: "block",
              width: "100%",
              px: 1.5,
              py: 1,
              color: "rgba(255,255,255,0.8)",
              bgcolor: "#000",
            }}
          >
            {title}
          </Typography>
        </Box>
      )}
    </Dialog>
  );
}

const SYNC_STATUS_KEYS = {
  running: "adDelivery.syncStatus.running",
  ok: "adDelivery.syncStatus.ok",
  partial: "adDelivery.syncStatus.partial",
  failed: "adDelivery.syncStatus.failed",
} as const satisfies Record<VistarCatalogueSyncView["status"], string>;

const STOCKHOLM_SHORT: Intl.DateTimeFormatOptions = {
  timeZone: "Europe/Stockholm",
  dateStyle: "short",
  timeStyle: "short",
};

/**
 * The line above the report that says where creative names come from and
 * how fresh they are, with "Sync now". Names come from Vistar's creative
 * catalogue, synced daily; a sync on demand is for a campaign that started
 * today.
 */
export function VistarCatalogueBar({
  status,
  syncing,
  error,
  onSync,
}: {
  status: VistarCatalogueStatus | null;
  /** A sync is in progress (started here, or already running). */
  syncing: boolean;
  error: string | null;
  onSync: () => void;
}) {
  const { t } = useTranslation("analytics");
  const f = useFormat();
  const pf = usePlaybackFormat();
  const last = status?.lastSync ?? null;
  let line: string;
  if (!status) line = t("adDelivery.catalogue.loading");
  else if (!status.configured) line = t("adDelivery.catalogue.notConfigured");
  else if (syncing) line = t("adDelivery.catalogue.syncing");
  else if (!last)
    line = t("adDelivery.catalogue.notSynced", {
      venues: pf.count(status.venues),
    });
  else {
    const facts = {
      creatives: pf.count(status.creatives),
      status: t(SYNC_STATUS_KEYS[last.status]),
      when: f.dateTime(last.finishedAt ?? last.startedAt, STOCKHOLM_SHORT),
    };
    line =
      last.trigger === "manual" && last.triggeredBy
        ? t("adDelivery.catalogue.syncedBy", {
            ...facts,
            user: last.triggeredBy,
          })
        : t("adDelivery.catalogue.synced", facts);
  }
  const failed =
    !syncing && last && (last.status === "failed" || last.status === "partial");
  return (
    <Box data-testid="ads-vistar-catalogue">
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
      >
        <Typography
          variant="bodySm"
          sx={{ color: tokens.inkMuted }}
          data-testid="ads-vistar-catalogue-status"
        >
          {line}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={onSync}
          disabled={!status?.configured || syncing}
          startIcon={
            syncing ? (
              <CircularProgress size={14} />
            ) : (
              <SyncIcon fontSize="small" />
            )
          }
          data-testid="ads-vistar-sync"
        >
          {syncing
            ? t("adDelivery.catalogue.syncingButton")
            : t("adDelivery.catalogue.syncNow")}
        </Button>
      </Stack>
      {failed && last?.error && (
        <Alert
          severity={last.status === "failed" ? "error" : "warning"}
          sx={{ mt: 1 }}
          data-testid="ads-vistar-catalogue-error"
        >
          {last.status === "failed"
            ? t("adDelivery.catalogue.lastFailed", { error: last.error })
            : t("adDelivery.catalogue.venuesFailed", {
                failed: pf.count(last.venuesFailed),
                asked: pf.count(last.venuesAsked),
                error: last.error,
              })}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
}

export function AdDeliveryView({
  report,
  compare,
}: {
  report: AdDeliveryReport;
  /** Show the previous period: only when it lies inside the data we have. */
  compare: boolean;
}) {
  const { t } = useTranslation(["analytics", "common"]);
  const f = useFormat();
  const pf = usePlaybackFormat();
  const { foyer, autoplayer, filters } = report;
  const totals = foyer.totals;
  const notPlayed = totals.failed + totals.expired + totals.unconfirmed;
  const prev = <T,>(v: T) => (compare ? v : undefined);
  const filterName = (k: string) =>
    (FILTER_KEYS as readonly string[]).includes(k)
      ? t(`adDelivery.filterNames.${k as (typeof FILTER_KEYS)[number]}`)
      : k;

  return (
    <Stack spacing={2.5} data-testid="ads-view">
      {report.notApplied.length > 0 && (
        <Typography
          variant="meta"
          sx={{ color: tokens.inkMuted }}
          data-testid="ads-not-applied"
        >
          {t("adDelivery.notApplied", {
            filters: report.notApplied.map(filterName).join(", "),
          })}
        </Typography>
      )}

      <Box>
        <Typography variant="subtitle1">
          {t("adDelivery.foyerTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t("adDelivery.foyerIntro")}
        </Typography>
        <Box
          sx={{
            display: "grid",
            gap: 1.5,
            gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))",
          }}
        >
          <KpiTile
            label={t("adDelivery.kpi.played")}
            value={pf.count(totals.played)}
            now={totals.played}
            before={prev(foyer.previous.played)}
            better="up"
            hint={t("adDelivery.kpi.playedHint")}
            testId="ads-kpi-played"
          />
          <KpiTile
            label={t("adDelivery.kpi.adTime")}
            value={pf.adTime(totals.adSeconds)}
            now={totals.adSeconds}
            before={prev(foyer.previous.adSeconds)}
            better="up"
            testId="ads-kpi-time"
          />
          <KpiTile
            label={t("adDelivery.kpi.fillRate")}
            value={pf.percent(totals.fillRate)}
            now={totals.fillRate}
            better="up"
            hint={
              totals.slots > 0
                ? t("adDelivery.kpi.fillHint", {
                    filled: pf.adTime(totals.secondsFilled),
                    asked: pf.adTime(totals.secondsAsked),
                    slotsFilled: pf.count(totals.slotsFilled),
                    slots: pf.count(totals.slots),
                  })
                : t("adDelivery.kpi.noSlots")
            }
            testId="ads-kpi-fill"
          />
          <KpiTile
            label={t("adDelivery.kpi.screens")}
            value={pf.count(totals.screens)}
            now={totals.screens}
            better="up"
            hint={t("adDelivery.kpi.screensHint", { count: totals.premises })}
            testId="ads-kpi-screens"
          />
          <KpiTile
            label={t("adDelivery.kpi.notPlayed")}
            value={pf.count(notPlayed)}
            now={notPlayed}
            better="down"
            hint={t("adDelivery.kpi.notPlayedHint", {
              unconfirmed: pf.count(totals.unconfirmed),
              failed: pf.count(totals.failed),
              expired: pf.count(totals.expired),
            })}
            testId="ads-kpi-not-played"
          />
        </Box>
      </Box>

      <Section title={t("adDelivery.perDay")} testId="ads-trend">
        <TrendLine
          points={fillDays(pf, filters.from, filters.to, foyer.series)}
          format={pf.count}
          showPrevious={false}
          ariaLabel={t("adDelivery.perDay")}
        />
      </Section>

      <Section
        title={t("adDelivery.byCreative")}
        subtitle={t("adDelivery.byCreativeSubtitle")}
        testId="ads-creatives"
      >
        <SimpleTable
          rows={foyer.creatives}
          rowKey={(r) => r.key}
          empty={t("adDelivery.noAds")}
          columns={[
            {
              label: t("adDelivery.columns.creative"),
              cell: (r) => <CreativeCell c={r} />,
            },
            {
              label: t("adDelivery.columns.plays"),
              align: "right",
              cell: (r) => pf.count(r.played),
            },
            ...(compare
              ? [
                  {
                    label: t("adDelivery.columns.change"),
                    align: "right" as const,
                    cell: (
                      r: AdDeliveryReport["foyer"]["creatives"][number]
                    ) => (
                      <ChangeBadge
                        now={r.played}
                        before={r.previousPlayed}
                        better="up"
                      />
                    ),
                  },
                ]
              : []),
            {
              label: t("adDelivery.columns.adTime"),
              align: "right",
              cell: (r) => pf.adTime(r.adSeconds),
            },
            {
              label: t("adDelivery.columns.screens"),
              align: "right",
              cell: (r) => pf.count(r.screens),
            },
            {
              label: t("adDelivery.columns.premises"),
              align: "right",
              cell: (r) => pf.count(r.premises),
            },
          ]}
        />
      </Section>

      <Box
        sx={{
          display: "grid",
          gap: 2.5,
          gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
        }}
      >
        <Section title={t("adDelivery.byPremise")} testId="ads-premises">
          <SimpleTable
            rows={foyer.premises}
            rowKey={(r) => r.premiseId}
            empty={t("adDelivery.noPremiseAsked")}
            columns={[
              { label: t("adDelivery.columns.premise"), cell: (r) => r.name },
              {
                label: t("adDelivery.columns.plays"),
                align: "right",
                cell: (r) => pf.count(r.played),
              },
              {
                label: t("adDelivery.columns.adTime"),
                align: "right",
                cell: (r) => pf.adTime(r.adSeconds),
              },
              {
                label: t("adDelivery.columns.fill"),
                align: "right",
                cell: (r) => pf.percent(r.fillRate),
              },
              {
                label: t("adDelivery.columns.notPlayed"),
                align: "right",
                cell: (r) => pf.count(r.notPlayed),
              },
            ]}
          />
        </Section>
        <Section title={t("adDelivery.byScreen")} testId="ads-screens">
          <SimpleTable
            rows={foyer.screens}
            rowKey={(r) => r.screenId}
            empty={t("adDelivery.noScreenAsked")}
            columns={[
              {
                label: t("adDelivery.columns.screen"),
                cell: (r) => (
                  <Box>
                    <Typography variant="body2">{r.screenId}</Typography>
                    <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
                      {r.premiseName}
                    </Typography>
                  </Box>
                ),
              },
              {
                label: t("adDelivery.columns.plays"),
                align: "right",
                cell: (r) => pf.count(r.played),
              },
              {
                label: t("adDelivery.columns.fill"),
                align: "right",
                cell: (r) => pf.percent(r.fillRate),
              },
              {
                label: t("adDelivery.columns.notPlayed"),
                align: "right",
                cell: (r) => pf.count(r.notPlayed),
              },
              {
                label: t("adDelivery.columns.lastPlayed"),
                align: "right",
                cell: (r) =>
                  r.lastPlayedAt
                    ? f.dateTime(r.lastPlayedAt, STOCKHOLM_SHORT)
                    : EMPTY,
              },
            ]}
          />
        </Section>
      </Box>

      <Section
        title={t("adDelivery.autoplayer.title")}
        subtitle={t("adDelivery.autoplayer.subtitle")}
        testId="ads-autoplayer"
      >
        {autoplayer.available ? (
          <Stack spacing={2}>
            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
              <Box>
                <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
                  {t("adDelivery.autoplayer.starts")}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography
                    sx={{
                      typography: "headline",
                      fontWeight: "fontWeightSemibold",
                    }}
                    data-testid="ads-autoplayer-starts"
                  >
                    {pf.count(autoplayer.starts)}
                  </Typography>
                  {compare && (
                    <ChangeBadge
                      now={autoplayer.starts}
                      before={autoplayer.previousStarts}
                      better="up"
                    />
                  )}
                </Stack>
              </Box>
              <Box>
                <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
                  {t("adDelivery.autoplayer.premises")}
                </Typography>
                <Typography
                  sx={{
                    typography: "headline",
                    fontWeight: "fontWeightSemibold",
                  }}
                >
                  {pf.count(autoplayer.premises)}
                </Typography>
              </Box>
            </Stack>
            <SimpleTable
              rows={autoplayer.creatives.slice(0, 50)}
              rowKey={(r) => r.key}
              empty={
                autoplayer.dataSince
                  ? t("adDelivery.autoplayer.noStarts")
                  : t("adDelivery.autoplayer.noData")
              }
              testId="ads-autoplayer-creatives"
              columns={[
                {
                  label: t("adDelivery.columns.creative"),
                  cell: (r) => <CreativeCell c={r} />,
                },
                {
                  label: t("adDelivery.columns.starts"),
                  align: "right",
                  cell: (r) => pf.count(r.starts),
                },
                ...(compare
                  ? [
                      {
                        label: t("adDelivery.columns.change"),
                        align: "right" as const,
                        cell: (r: (typeof autoplayer.creatives)[number]) => (
                          <ChangeBadge
                            now={r.starts}
                            before={r.previousStarts}
                            better="up"
                          />
                        ),
                      },
                    ]
                  : []),
                {
                  label: t("adDelivery.columns.premises"),
                  align: "right",
                  cell: (r) => pf.count(r.premises),
                },
              ]}
            />
            {autoplayer.dataSince && (
              <Typography variant="micro" sx={{ color: tokens.inkFaint }}>
                {t("adDelivery.autoplayer.collectedSince", {
                  date: pf.day(autoplayer.dataSince),
                })}
              </Typography>
            )}
          </Stack>
        ) : (
          <Alert severity="info" data-testid="ads-autoplayer-unavailable">
            {autoplayer.reason === "unconfigured"
              ? t("adDelivery.autoplayer.unconfigured")
              : t("adDelivery.autoplayer.unavailable")}
          </Alert>
        )}
      </Section>

      <Typography
        variant="caption"
        color="text.secondary"
        component="p"
        data-testid="ads-footnote"
      >
        {foyer.dataSince
          ? t("adDelivery.footnote.since", { date: pf.day(foyer.dataSince) })
          : t("adDelivery.footnote.none")}{" "}
        {t("adDelivery.footnote.rest")}
      </Typography>
    </Stack>
  );
}
